import { useRef, useId } from 'react';
import PropTypes from 'prop-types';
import useModalA11y from '../../hooks/useModalA11y';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../buttons/IconButton';

/**
 * MobileSheet (TD-2576 Phase 2)
 *
 * Shared mobile bottom-sheet primitive. A native `<dialog>` opened with
 * `showModal()` through `useModalA11y`, so it lives in the browser **top layer**
 * (escapes any transformed / scrolled ancestor), dims the page with its
 * `::backdrop`, and gets Esc / focus-trap / body-scroll-lock for free. Backs the
 * results surfaces' **View / Sort / Customize** menus (Option B) and any other
 * bottom-sheet content. Generalises the enrichment `KeyContactsMobileSheet` /
 * `.kc-sheet` (TD-2485) and the Add-filter drawer (TD-2756).
 *
 * The `<dialog>` is a transparent, full-viewport click-to-dismiss surface; the
 * visible sheet is `.mobile-sheet-panel` pinned to the bottom — a grabber, a head
 * (title + close control), a scrollable body, and an optional footer.
 */
const MobileSheet = ({ isOpen, onClose, title, children, footer = null }) => {
  const dialogRef = useRef(null);
  const titleId = useId();
  useModalA11y({ isOpen, onClose, dialogRef });

  if (!isOpen) return null;

  return (
    <dialog ref={dialogRef} className="mobile-sheet" aria-labelledby={titleId}>
      <div className="mobile-sheet-panel">
        <span className="mobile-sheet-grabber" aria-hidden="true" />
        <div className="mobile-sheet-head">
          <h3 className="mobile-sheet-title" id={titleId}>
            {title}
          </h3>
          <IconButton
            type={ICON_BUTTON_TYPES.BLUE}
            icon={ICON_BUTTON_ICONS.X_LG}
            withFill={false}
            withBorder={false}
            tooltipText="Close"
            wrapClassName="mobile-sheet-close"
            // Call onClose() with no argument so the ✕ matches every other
            // dismiss path (Esc / backdrop / hook), which honour the
            // useModalA11y `onClose(): void` contract — IconButton would
            // otherwise forward the click event as the first argument.
            onClick={() => onClose()}
          />
        </div>
        <div className="mobile-sheet-body">{children}</div>
        {footer ? <div className="mobile-sheet-foot">{footer}</div> : null}
      </div>
    </dialog>
  );
};

MobileSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  footer: PropTypes.node
};

export default MobileSheet;
